import { useMemo, useRef } from "react";
import { useLocSettings } from "../hooks/SettingsHooks";
import PropTypes from "prop-types";
import classNames from "classnames";
import { encodeFilterQstring } from "../utils/ProductsUtils";
import { useHistory } from "react-router";
import { useMediaQuery } from "react-responsive";
import {
  useBjelinTranslations,
  useSpecsTranslation,
  useUnitTranslation
} from "../hooks/TranslationHooks";
import COLORS from "../config/colors";
import { useTooltip } from "./ToolTip";
import { useLang } from "../hooks/LangHooks";

const TooltipContent = ({
  field,
  value,
  unit,
  valueDescription,
  clickToFilter = false,
  filterText
}) => {
  return (
    <div key="specTooltip">
      <div>
        <h4>
          {field}: {value} {unit}
        </h4>
        <div dangerouslySetInnerHTML={{ __html: valueDescription }} />
        {clickToFilter && <div>{filterText}</div>}
      </div>
    </div>
  );
};

const IconWithPopup = ({
  field,
  value,
  unit,
  color,
  showLabel = false,
  sku,
  productCategoryUrl = "",
  noTooltip = false,
  clickToFilter = false
}) => {
  const settings = useLocSettings();
  const history = useHistory();
  const hasHover = useMediaQuery({ query: "(hover: hover)" });
  const t = useBjelinTranslations();
  const ts = useSpecsTranslation();
  const tu = useUnitTranslation();
  const tooltipParent = useRef();
  const lang = useLang();

  const icon = settings.specifications?.[field]?.icons?.[value];
  const valueDescription =
    settings.specifications?.[field]?.value_descriptions?.[value]?.description;
  const description = settings.specifications?.[field]?.description;
  const id =
    "icon_" +
    sku +
    "_" +
    (showLabel ? "l" : "n") +
    "_" +
    field.replace(/[^a-zA-Z0-9]/g, "") +
    "_" +
    value.replace(/[^a-zA-Z0-9]/g, "");

  const tooltipContent = useMemo(
    () =>
      noTooltip ? null : (
        <TooltipContent
          field={ts(field)}
          value={ts(field, value)}
          unit={unit && tu(unit)}
          valueDescription={valueDescription}
          description={description}
          clickToFilter={clickToFilter}
          filterText={t("click.to.filter")}
          clickText={t("click.for.more")}
        />
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [lang, clickToFilter, field, unit, value, noTooltip]
  );

  useTooltip(tooltipParent, tooltipContent);

  const filterClick = e => {
    if (!noTooltip) {
      e.preventDefault();
      e.stopPropagation();
      if (hasHover && clickToFilter) {
        history.push(
          productCategoryUrl +
            "?" +
            new URLSearchParams({
              filter: encodeFilterQstring({ [field]: [value] })
            })
        );
      }
    }
  };

  if (icon) {
    return (
      <>
        <span
          className={classNames("spec-wrapper", { "with-label": showLabel })}
        >
          <span
            ref={tooltipParent}
            className={"spec-icon"}
            style={{
              color: color ? color : icon.color || COLORS.bjelin_grey,
              ...((clickToFilter || (!!description && !noTooltip)) && {
                cursor: "pointer"
              })
            }}
            dangerouslySetInnerHTML={{ __html: icon.svg2 || icon.svg }}
            id={id}
            onClick={filterClick}
          />
          {showLabel && (
            <span className={"spec-icon-label"}>
              {ts(field, value)} {unit && tu(unit)}
            </span>
          )}
        </span>
      </>
    );
  }
  return null;
};

IconWithPopup.propTypes = {
  field: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  showLabel: PropTypes.bool,
  clickToFilter: PropTypes.bool,
  color: PropTypes.string,
  productCategoryUrl: PropTypes.string
};

export default IconWithPopup;
